import AmendmentOriginalButton from "./AmendmentOriginalButton";
/**
 * 품목분류사례 탐색기 (?audit=precedents) — CielHS(CL) 벤치마킹.
 *
 * 2-뷰 모드:
 *   ① 그리드(기본 진입): 시행일 desc 전체 목록. 컬럼 HSK|품명|구분|시행일 + NEW 뱃지.
 *      국내/해외 소스 토글 + 검색바(HSK/품명). 서버 페이지네이션(GET precedents-recent).
 *      검색 시엔 GET precedents-search 결과를 같은 표에 렌더(클라 페이지네이션).
 *   ② 상세(행 선택): 좌=세번·품명 리스트 / 우=품목분류사례 상세(DetailPane) +
 *      하단 액션바 [내 품목·내 사례철·인쇄하기·이전 화면]. 이전 화면=현재 검색 목록 복귀.
 *
 * - 딥링크: ?audit=precedents&case=<caseId> (pushState·popstate 동기화).
 * - keep-alive 마운트(App.js, display:none 토글) — 자체 height 100%.
 * - 내 품목 버튼=watchlist(세번 단위) 재사용. 장식 이모지 금지(모노 기호만).
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Search } from "lucide-react";
import { CLIP_IMG_BASE } from "./precedentAssets";
import { useWatchlist } from "./watchlistApi";
import { useSavedCases } from "./savedCasesApi";
import { publishPageShare } from "./shareSnapshotStore";
import AmendmentReason from "./AmendmentReason";
import PrecedentIdentity from "./PrecedentIdentity";
import SourceEvidenceLink, {
  precedentEvidence,
} from "./SourceEvidenceLink";
import {
  navigateToReturnTarget,
  normalizeReturnTo,
  readPrecedentNavigation,
} from "./precedentNavigation";
import "./theme.css";

const PAGE_SIZE = 30; // 그리드 페이지당 행 수(recent=서버, search=클라)
const SEARCH_LIMIT = 200; // 검색 시 서버에서 가져오는 최대 건수
const NEW_DAYS = 30; // 피드 최신일 기준 NEW 뱃지 임계

// HSK → NNNN.NN-NNNN (10자리), 부분 입력도 최대한 포맷
function formatHsk(hsk) {
  const d = String(hsk == null ? "" : hsk).replace(/[^0-9]/g, "");
  if (d.length >= 10)
    return `${d.slice(0, 4)}.${d.slice(4, 6)}-${d.slice(6, 10)}`;
  if (d.length >= 6) return `${d.slice(0, 4)}.${d.slice(4, 6)}`;
  if (d.length >= 4) return d.slice(0, 4);
  return d || "—";
}

// 입력만 보고 검색 종류 자동판별 — 숫자(구분자 .-공백 허용)만이면 세번(HSK), 그 외는 품명·내용.
//   토글 버튼 없이 단일 검색창(CLHS·CLIP식). "8471"→HSK, "핸드백"·"8471 케이스"→품명·내용.
function detectMode(raw) {
  const s = String(raw == null ? "" : raw).trim();
  const digits = s.replace(/[^0-9]/g, "");
  const nonSep = s.replace(/[\s.\-]/g, ""); // 구분자 제거 후 남는 문자
  return digits.length >= 2 && digits.length === nonSep.length ? "hsk" : "name";
}

function ymd(v) {
  const s = String(v == null ? "" : v);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

// 관세청 결정문(물품설명·결정사유)은 크롤 과정에서 줄바꿈이 유실돼 한 문단으로 뭉쳐 저장됨.
// 렌더 단계에서 목록 마커 앞 줄바꿈을 복원 — CLHS식 왼쪽 정렬(표시 전용, 데이터 무변경).
//   ㅇ = 최상위 항목(앞에 빈 줄) · - = 하위 항목(새 줄) · ※ = 비고(빈 줄).
// ⚠"ㅇ"(U+3147)은 한글 단어에 단독 등장하지 않아 안전. "-"는 앞뒤 공백일 때만 분할(세번
//   "0804.20-0000"처럼 공백 없는 하이픈은 오분할 안 됨).
export function formatRulingText(s) {
  if (s == null) return s;
  return String(s)
    .replace(/\r/g, "")
    .replace(/\s*ㅇ\s+(?=[가-힣])/g, "\n\nㅇ ") // 최상위 항목(뒤가 한글일 때만)
    .replace(/ +-\s+(?=[가-힣])/g, "\n- ") // 하위 항목(앞뒤 공백+뒤 한글 → 영문 대시·세번 하이픈 오분할 방지)
    .replace(/\s*※\s*/g, "\n\n※ ") // 비고
    .replace(/^[\s\n]+/, "");
}

/**
 * 68차(2026-09-17): 영문 결정문은 크롤에서 줄바꿈이 사라져 한 덩어리로 온다(사용자: "이렇게 되어 있으면 보기 힘들지 않나").
 * 미국 CBP·EU 결정문의 관례 절 제목(FACTS: · ISSUE: · LAW AND ANALYSIS: · HOLDING: …)으로 절을 나눈다.
 * 글자는 하나도 바꾸지 않고 자르기만 한다 — 없는 문단을 만들어 넣지 않는다(법령 원문이므로).
 */
const RULING_SECTIONS = [
  ["LAW AND ANALYSIS", "법령·분석"],
  ["EFFECT ON OTHER RULINGS", "다른 결정에 대한 효력"],
  ["MERCHANDISE AT ISSUE", "대상 물품"],
  ["ANALYSIS", "분석"],
  ["BACKGROUND", "배경"],
  ["DECISION", "결정"],
  ["FACTS", "사실관계"],
  ["HOLDINGS", "결론"],
  ["HOLDING", "결론"],
  ["ISSUES", "쟁점"],
  ["ISSUE", "쟁점"],
];
const SECTION_KO = new Map(RULING_SECTIONS);
const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** 본문 → [{label, ko, body}]. 절 제목이 없으면 [{label:null, body}] 하나. */
export function splitRulingSections(text) {
  const s = String(text == null ? "" : text).replace(/\r/g, "").trim();
  if (!s) return [];
  const alts = RULING_SECTIONS.map(([en]) => esc(en)).join("|");
  const re = new RegExp(`(^|[\\s.;:”"'])(${alts})\\s*:`, "g");
  const cuts = [];
  let m;
  while ((m = re.exec(s)) !== null) cuts.push({ at: m.index + m[1].length, label: m[2], end: re.lastIndex });
  if (!cuts.length) return [{ label: null, ko: null, body: s }];
  const out = [];
  const head = s.slice(0, cuts[0].at).trim();
  if (head) out.push({ label: null, ko: null, body: head });
  cuts.forEach((c, i) => {
    const body = s.slice(c.end, i + 1 < cuts.length ? cuts[i + 1].at : s.length).trim();
    out.push({ label: c.label, ko: SECTION_KO.get(c.label) || null, body });
  });
  return out;
}

/** 절 안에서 편지 부분(Re: · Dear …: · Sincerely)만 줄을 나눈다 — 문장을 임의로 끊지 않는다. */
export function splitLetterLines(body) {
  return String(body == null ? "" : body)
    .replace(/\s+(?=Re:\s)/g, "\n")
    .replace(/\s+(?=Dear\s[^:\n]{0,60}:)/g, "\n")
    .replace(/(Dear\s[^:\n]{0,60}:)\s+/g, "$1\n")
    .replace(/\s+(?=Sincerely\b)/g, "\n")
    .split(/\n+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

// 세번(6206.40.3030 · 62.11)은 이 글을 읽는 이유다 — 눈에 걸리도록 고정폭·굵게(색은 안 쓴다).
const CODE_RE = /(\b\d{4}\.\d{2}(?:\.\d{2,4})?\b|\b\d{2}\.\d{2}\b)/g;
function MarkCodes({ text }) {
  const parts = String(text == null ? "" : text).split(CODE_RE);
  return (
    <>
      {parts.map((p, i) => (i % 2 === 1
        ? <span key={i} style={{ fontFamily: "JetBrains Mono, monospace", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{p}</span>
        : <React.Fragment key={i}>{p}</React.Fragment>))}
    </>
  );
}

/** 절 제목이 있으면 절로 나눠 보이고, 없으면 종전 렌더. 영문은 한 줄 길이를 줄여 읽기 쉽게 한다. */
function RulingBody({ text, empty = "—" }) {
  const sections = splitRulingSections(text);
  const labeled = sections.filter((x) => x.label);
  if (!labeled.length) return <RulingParagraphs text={text} empty={empty} />;
  return (
    <div data-testid="ruling-sections" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {labeled.length > 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: 11.5, color: "var(--audit-text-muted)" }}>
          <span style={{ alignSelf: "center" }}>절</span>
          {labeled.map((x) => (
            <a key={x.label} href={`#ruling-${x.label.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={(e) => { e.preventDefault(); document.getElementById(`ruling-${x.label.replace(/\s+/g, "-").toLowerCase()}`)?.scrollIntoView({ block: "start", behavior: "smooth" }); }}
              style={{ border: "1px solid var(--audit-border)", borderRadius: 999, padding: "1px 9px", color: "inherit", textDecoration: "none", cursor: "pointer" }}>
              {x.ko || x.label}
            </a>
          ))}
        </div>
      )}
      {sections.map((x, i) => (
        <section key={`${x.label || "head"}-${i}`} id={x.label ? `ruling-${x.label.replace(/\s+/g, "-").toLowerCase()}` : undefined} data-testid={x.label ? "ruling-section" : "ruling-head"} data-label={x.label || undefined} style={{ scrollMarginTop: 12 }}>
          {x.label && (
            <h4 style={{ margin: "0 0 5px", fontSize: 12, fontWeight: 750, color: "var(--audit-text)", letterSpacing: ".02em" }}>
              {x.ko || x.label}<span style={{ marginLeft: 6, fontSize: 11, fontWeight: 500, color: "var(--audit-text-muted)" }}>{x.label}</span>
            </h4>
          )}
          <div style={{ maxWidth: "72ch", lineHeight: 1.8, color: x.label ? "var(--audit-text)" : "var(--audit-text-muted)" }}>
            {splitLetterLines(x.body).map((line, j) => (
              <p key={j} style={{ margin: j ? "6px 0 0" : 0 }}><MarkCodes text={line} /></p>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function RulingParagraphs({ text, empty = "—" }) {
  const normalized = formatRulingText(text);
  if (!normalized) return <span style={{ color: "var(--audit-text-faint)" }}>{empty}</span>;
  const lines = normalized.split(/\n+/).map((line) => line.trim()).filter(Boolean);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {lines.map((line, i) => {
        const top = line.startsWith("ㅇ");
        const child = line.startsWith("-");
        const note = line.startsWith("※");
        const marker = top ? "ㅇ" : child ? "–" : note ? "※" : null;
        return (
          <div
            key={`${i}-${line.slice(0, 16)}`}
            style={{
              display: marker ? "grid" : "block",
              gridTemplateColumns: marker ? "16px minmax(0, 1fr)" : undefined,
              gap: marker ? 4 : undefined,
              paddingLeft: child ? 18 : 0,
              marginTop: top && i > 0 ? 5 : 0,
              lineHeight: 1.65,
              color: note ? "var(--audit-text-muted)" : "var(--audit-text)",
            }}
          >
            {marker && <span aria-hidden="true" style={{ color: "var(--audit-text-muted)", fontWeight: top ? 700 : 500 }}>{marker}</span>}
            <span>{marker ? line.slice(1).trim() : line}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * 해외사례 본문: 관세당국 원문과 관세청 자동번역 국문을 전환해 읽는다. 원문이 있으면 원문이 기본이다
 * (자동번역은 "Ash City → 물푸레나무 도시"처럼 품질이 고르지 않다). 하나만 있으면 전환 없이 그것을 보인다.
 */
function ForeignTextCard({ title, original, korean, fallback, emphasized = false, testId }) {
  const hasOriginal = Boolean(String(original || "").trim());
  const hasKorean = Boolean(String(korean || "").trim());
  const [lang, setLang] = useState(hasOriginal ? "original" : "korean");
  const showOriginal = lang === "original" && hasOriginal;
  const text = showOriginal ? original : (hasKorean ? korean : (hasOriginal ? original : fallback));
  const note = showOriginal
    ? "관세당국 결정문 원문"
    : hasKorean ? "관세청 자동번역 국문 전문" : (hasOriginal ? "" : "목록 적재 문언 (원문 미확보)");
  return (
    <section
      data-testid={testId}
      style={{
        padding: "12px 14px",
        border: `1px solid ${emphasized ? "var(--audit-border-strong)" : "var(--audit-border)"}`,
        borderRadius: 8,
        background: emphasized ? "var(--audit-bg-subtle)" : "var(--audit-bg)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 14, color: "var(--audit-text)", fontWeight: 750 }}>{title}</h3>
        {hasOriginal && hasKorean && (
          <span role="group" aria-label="본문 언어" style={{ display: "inline-flex", gap: 2, marginLeft: "auto" }}>
            {[["original", "원문"], ["korean", "국문"]].map(([id, label]) => (
              <button
                key={id}
                type="button"
                aria-pressed={lang === id}
                onClick={() => setLang(id)}
                style={{
                  border: `1px solid ${lang === id ? "var(--audit-accent)" : "var(--audit-border)"}`,
                  background: lang === id ? "var(--audit-accent-soft, #eff6ff)" : "transparent",
                  color: lang === id ? "var(--audit-accent)" : "var(--audit-text-muted)",
                  borderRadius: 999, padding: "2px 10px", fontSize: 11.5, fontWeight: lang === id ? 700 : 500,
                  cursor: "pointer", fontFamily: "inherit",
                }}
              >
                {label}
              </button>
            ))}
          </span>
        )}
        {note && <span style={{ fontSize: 11, color: "var(--audit-text-muted)" }}>{note}</span>}
      </div>
      <div style={{ fontSize: 13, maxWidth: 800 }}>
        <RulingBody text={text} />
      </div>
    </section>
  );
}

function DetailTextCard({ title, children, emphasized = false }) {
  return (
    <section
      style={{
        padding: "12px 14px",
        border: `1px solid ${emphasized ? "var(--audit-border-strong)" : "var(--audit-border)"}`,
        borderRadius: 8,
        background: emphasized ? "var(--audit-bg-subtle)" : "var(--audit-bg)",
      }}
    >
      <h3 style={{ margin: "0 0 8px", fontSize: 14, color: "var(--audit-text)", fontWeight: 750 }}>{title}</h3>
      <div style={{ fontSize: 13, maxWidth: 800 }}>{children}</div>
    </section>
  );
}

// ISO(YYYY-MM-DD)에서 days 뺀 날짜 문자열 — NEW 뱃지 컷오프.
function isoMinusDays(iso, days) {
  const d = new Date(iso + "T00:00:00Z");
  if (isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

// 구분(구분번호) — 서버 gubun 우선, 없으면 국가코드/case_id 접두 제거 폴백(검색결과용).
const CID_PREFIXES = ["BD-", "KCS-", "CLIPF-", "CLIP-"];
function deriveGubun(it) {
  if (it.gubun) return it.gubun;
  if (it.country) return it.country;
  const cid = String(it.case_id || "");
  for (const p of CID_PREFIXES) if (cid.startsWith(p)) return cid.slice(p.length);
  return cid || "—";
}

// 국내/해외 판별 — clip_foreign(또는 country 보유)=해외, 그 외(clip_kcs·bigdata 등)=국내.
function isForeignCase(it) {
  if (it && it.source) return it.source === "clip_foreign";
  return !!(it && it.country);
}

// 국내/해외 뱃지 — 검색결과가 국내/해외 섞일 때 구분(사용자 요청).
function SourceBadge({ foreign }) {
  return (
    <span
      style={{
        fontSize: 11.5,
        fontWeight: 700,
        padding: "1px 5px",
        borderRadius: 3,
        background: "var(--audit-bg-subtle)",
        color: "var(--audit-text-muted)",
        border: "1px solid var(--audit-border)",
        whiteSpace: "nowrap",
      }}
    >
      {foreign ? "해외" : "국내"}
    </span>
  );
}

// 검색어 하이라이트(관세청식 노란 배경) — 질의 + 공백분리 토큰 매칭. query 없으면 원문.
function highlightText(text, query) {
  const s = String(text == null ? "" : text);
  const q = String(query == null ? "" : query).trim();
  if (!q || !s) return s;
  const terms = [...new Set([q, ...q.split(/\s+/)].filter((t) => t.length >= 2))];
  if (terms.length === 0) return s;
  const escRe = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp("(" + terms.map(escRe).join("|") + ")", "gi");
  const lower = new Set(terms.map((t) => t.toLowerCase()));
  return s.split(re).map((p, i) =>
    lower.has(p.toLowerCase()) ? (
      <mark key={i} style={{ background: "#fde68a", color: "inherit", padding: "0 1px", borderRadius: 2 }}>
        {p}
      </mark>
    ) : (
      p
    ),
  );
}

// 품명(title)에 검색어(또는 공백분리 토큰)가 실제로 들어있는지 — highlightText 와 동일 토큰화.
// false = 품명엔 없고 물품설명/결정사유(전문)에서 걸린 것 → "본문 일치" 표시용.
function titleHasQuery(text, query) {
  const s = String(text == null ? "" : text).toLowerCase();
  const q = String(query == null ? "" : query).trim().toLowerCase();
  if (!q || !s) return true; // 검색어 없음 → 배지 안 뜸
  const terms = [...new Set([q, ...q.split(/\s+/)].filter((t) => t.length >= 2))];
  return terms.length === 0 || terms.some((t) => s.includes(t));
}

// "본문 일치" 뱃지 — 품명이 아닌 물품설명·결정사유(전문)에서 검색어가 걸린 사례임을 표시(사용자 요청).
function BodyMatchBadge() {
  return (
    <span
      title="품명이 아니라 물품설명·결정사유(전문)에서 검색어가 발견된 사례입니다."
      style={{
        fontSize: 11.5,
        fontWeight: 700,
        padding: "1px 5px",
        borderRadius: 3,
        background: "#fefce8",
        color: "#a16207",
        border: "1px solid var(--audit-border)",
        whiteSpace: "nowrap",
        marginLeft: 6,
        verticalAlign: "middle",
      }}
    >
      본문 일치
    </span>
  );
}

// 본문 일치 스니펫 — 품명이 아닌 전문(결정사유·물품설명)에서 검색어가 걸린 실제 조각을 노란 하이라이트로 노출(사용자 요청).
function BodyMatchSnippet({ snippet, query }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 6,
        marginTop: 3,
        fontSize: 11.5,
        color: "var(--audit-text-muted)",
        lineHeight: 1.45,
      }}
    >
      <span
        style={{
          flexShrink: 0,
          fontSize: 11.5,
          fontWeight: 700,
          padding: "1px 5px",
          borderRadius: 3,
          background: "#fefce8",
          color: "#a16207",
          border: "1px solid var(--audit-border)",
          whiteSpace: "nowrap",
        }}
      >
        본문
      </span>
      <span
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {highlightText(snippet, query)}
      </span>
    </div>
  );
}

// 상세를 새 창에 인쇄용 HTML 로 렌더 후 print — 앱 레이아웃과 무간섭.
function printDetail(detail) {
  if (!detail) return;
  const esc = (s) =>
    String(s == null ? "" : s).replace(/[&<>]/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]),
    );
  const hsk = formatHsk(detail.hsk);
  const imgs = (Array.isArray(detail.images) ? detail.images : [])
    .map(
      (a) =>
        `<img src="${CLIP_IMG_BASE + encodeURIComponent(a)}" style="width:120px;height:120px;object-fit:cover;border:1px solid #ccc;margin:4px" />`,
    )
    .join("");
  const rows = [
    ["참조번호", detail.ref_no],
    ["시행일자", ymd(detail.decision_date)],
    ["시행기관", detail.org],
    ["결정세번", detail.decided_code_foreign || hsk],
    ["품명", detail.title],
    ["품명(원문)", detail.title_original && detail.title_original !== detail.title ? detail.title_original : ""],
    ["물품설명", formatRulingText(detail.description)],
    ["결정사유", formatRulingText(detail.reason)],
  ]
    .filter(([, v]) => v)
    .map(
      ([k, v]) =>
        `<tr><th style="text-align:left;vertical-align:top;padding:6px 12px 6px 0;width:90px;color:#555;white-space:nowrap;font-weight:600">${esc(k)}</th><td style="padding:6px 0;white-space:pre-wrap;line-height:1.6">${esc(v)}</td></tr>`,
    )
    .join("");
  const w = window.open("", "_blank", "width=820,height=920");
  if (!w) return;
  w.document.write(
    `<!doctype html><html lang="ko"><head><meta charset="utf-8"><title>품목분류사례 ${esc(hsk)}</title></head>` +
      `<body style="font-family:'Malgun Gothic',AppleSDGothicNeo,sans-serif;padding:24px;color:#111">` +
      `<h1 style="font-size:20px;border-bottom:2px solid #333;padding-bottom:8px;margin:0 0 14px">품목분류사례 ${esc(hsk)}</h1>` +
      `<table style="width:100%;border-collapse:collapse;font-size:13px"><tbody>${rows}</tbody></table>` +
      (imgs ? `<div style="margin-top:14px">${imgs}</div>` : "") +
      `</body></html>`,
  );
  w.document.close();
  w.focus();
  setTimeout(() => {
    try {
      w.print();
    } catch (e) {
      /* 사용자가 창을 닫았을 수 있음 */
    }
  }, 400);
}

export default function PrecedentsPage({
  embedded = false,
  inlineHsk = null,
  inlineCaseId = null,
  inlineKeyword = "",
  inlineSource = "domestic",
  onInlineResultsChange = null,
}) {
  const normalizedInlineQuery = String(inlineHsk || "")
    .replace(/\D/g, "")
    .slice(0, 10);
  // 호 상세(4자리)와 세번 상세(10자리)가 동일한 최신 분할 화면을 쓴다.
  const inlineMode = /^(?:[0-9]{4}|[0-9]{10})$/.test(normalizedInlineQuery);
  const normalizedInlineCaseId = String(inlineCaseId || "").trim() || null;
  const normalizedInlineKeyword = String(inlineKeyword || "").trim();
  const initialNavigation = useRef(
    inlineMode
      ? {
          caseId: normalizedInlineCaseId,
          originQuery: normalizedInlineQuery,
          // 세번 상세 하단의 해외사례 탭은 같은 분할 화면을 해외 출처로 연다(문의 #74).
          originSource: inlineSource === "foreign" ? "foreign" : "domestic",
          returnTo: null,
          splitView: true,
        }
      : typeof window === "undefined"
        ? readPrecedentNavigation("")
        : readPrecedentNavigation(window.location.search),
  ).current;
  const [navigation, setNavigation] = useState(initialNavigation);
  const originQueryRef = useRef(initialNavigation.originQuery);
  const originSourceRef = useRef(initialNavigation.originSource);

  // 소스·피드
  const [source, setSource] = useState(initialNavigation.originSource); // "domestic" | "foreign"
  const [feedMode, setFeedMode] = useState(
    initialNavigation.originQuery ? "search" : "recent",
  ); // "recent" | "search"

  // 검색바
  const [searchMode, setSearchMode] = useState(
    inlineMode && normalizedInlineKeyword ? "name" : "hsk",
  ); // "hsk" | "name"
  const [input, setInput] = useState(
    inlineMode ? normalizedInlineKeyword : initialNavigation.originQuery,
  );
  const [searched, setSearched] = useState(!!initialNavigation.originQuery);

  // 목록
  const [items, setItems] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listErr, setListErr] = useState(null);

  // recent 페이지네이션(서버)
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [feedMaxDate, setFeedMaxDate] = useState(null);

  // search 페이지네이션(클라)
  const [searchPage, setSearchPage] = useState(1);

  // 상세
  const [selectedCaseId, setSelectedCaseId] = useState(initialNavigation.caseId);
  const [detail, setDetail] = useState(null);
  // 상단 [공유] 가 지금 연 사례를 공유한다(2026-10-13) — 사례 번호만 보내고 내용은 서버가 원장에서 채운다
  const shareCaseId = detail && detail.case_id ? String(detail.case_id) : "";
  const shareTitle = detail && detail.title ? String(detail.title) : "";
  const shareCode = detail && detail.hsk ? String(detail.hsk) : "";
  useEffect(() => {
    publishPageShare("precedent", shareCaseId ? { type: "precedent", case_id: shareCaseId, query: shareTitle, title: shareTitle, code: shareCode } : null);
  }, [shareCaseId, shareTitle, shareCode]);
  useEffect(() => () => publishPageShare("precedent", null), []);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState(null);

  // 뷰 탭(전체목록 | 내 사례철) + 내 사례철 클라 페이지네이션
  const [viewTab, setViewTab] = useState("all"); // "all" | "saved"
  const [savedPage, setSavedPage] = useState(1);
  const sc = useSavedCases();

  const composingRef = useRef(false);
  const listScrollRef = useRef(null);
  const inlineResultsCallbackRef = useRef(onInlineResultsChange);

  useEffect(() => {
    inlineResultsCallbackRef.current = onInlineResultsChange;
  }, [onInlineResultsChange]);

  // ── 상세 fetch ─────────────────────────────────────────────
  const fetchDetail = useCallback((caseId) => {
    if (!caseId) return;
    setDetailLoading(true);
    setDetailErr(null);
    fetch(`/api/onto/precedent-detail?caseId=${encodeURIComponent(caseId)}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        setDetail(d);
        setDetailErr(null);
      })
      .catch((e) => {
        setDetail(null);
        setDetailErr(String(e && e.message ? e.message : e));
      })
      .finally(() => setDetailLoading(false));
  }, []);

  // ── recent 목록 fetch(서버 페이지네이션) ─────────────────────
  const fetchRecent = useCallback((src, pg) => {
    setListLoading(true);
    setListErr(null);
    const offset = (pg - 1) * PAGE_SIZE;
    const u = new URL("/api/onto/precedents-recent", window.location.origin);
    u.searchParams.set("source", src);
    u.searchParams.set("limit", String(PAGE_SIZE));
    u.searchParams.set("offset", String(offset));
    fetch(u.toString())
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        const its = Array.isArray(d && d.items) ? d.items : [];
        setItems(its);
        setHasMore(!!(d && d.hasMore));
        if (offset === 0) {
          if (typeof (d && d.total) === "number") setTotal(d.total);
          setFeedMaxDate(its[0] ? its[0].decision_date : null);
        }
        setListErr(null);
        if (listScrollRef.current) listScrollRef.current.scrollTop = 0;
      })
      .catch((e) => {
        setItems([]);
        setListErr(String(e && e.message ? e.message : e));
      })
      .finally(() => setListLoading(false));
  }, []);

  // ── 검색 fetch(기존 precedents-search 재사용) ────────────────
  const runSearch = useCallback((queryOverride) => {
    const raw = (typeof queryOverride === "string" ? queryOverride : input).trim();
    if (!raw && !inlineMode) {
      // 빈 검색 → recent 로 복귀
      setFeedMode("recent");
      setSearched(false);
      setPage(1);
      return;
    }
    if (typeof queryOverride === "string") setInput(raw);
    const mode = inlineMode
      ? (raw ? "name" : "hsk")
      : detectMode(raw); // 숫자=HSK, 그 외=품명·내용 자동판별(토글 제거)
    setSearchMode(mode); // 하이라이트/본문 스니펫 로직에서 참조
    setFeedMode("search");
    setSearched(true);
    setSearchPage(1);
    setListLoading(true);
    setListErr(null);
    const u = new URL("/api/onto/precedents-search", window.location.origin);
    u.searchParams.set("mode", "keyword");
    u.searchParams.set("limit", String(SEARCH_LIMIT));
    if (inlineMode) {
      u.searchParams.set("q", raw);
      if (normalizedInlineQuery.length >= 10) {
        u.searchParams.set("hsk", normalizedInlineQuery.slice(0, 10));
      } else {
        u.searchParams.set("hs4", normalizedInlineQuery.slice(0, 4));
      }
    } else if (mode === "hsk") {
      const d = raw.replace(/[^0-9]/g, "");
      u.searchParams.set("q", "");
      if (d.length >= 10) u.searchParams.set("hsk", d.slice(0, 10));
      else if (d.length >= 6) u.searchParams.set("hs6", d.slice(0, 6));
      else if (d.length >= 4) u.searchParams.set("hs4", d.slice(0, 4));
      else u.searchParams.set("hs4", d);
    } else {
      u.searchParams.set("q", raw);
    }
    fetch(u.toString())
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        const nextItems = Array.isArray(d && d.items) ? d.items : [];
        setItems(nextItems);
        if (inlineMode) {
          const visibleItems = nextItems.filter((it) =>
            source === "foreign" ? isForeignCase(it) : !isForeignCase(it),
          );
          inlineResultsCallbackRef.current?.({
            shown: visibleItems.length,
            limited: nextItems.length >= SEARCH_LIMIT,
          });
        }
        setListErr(null);
        if (listScrollRef.current) listScrollRef.current.scrollTop = 0;
      })
      .catch((e) => {
        setItems([]);
        setListErr(String(e && e.message ? e.message : e));
      })
      .finally(() => setListLoading(false));
  }, [inlineMode, input, normalizedInlineQuery, source]);

  // ── 사례 선택 (상세 모드 진입 + URL) ─────────────────────────
  const selectCase = useCallback(
    (caseId, { push = true } = {}) => {
      if (!caseId) return;
      setSelectedCaseId(caseId);
      fetchDetail(caseId);
      if (push && !inlineMode && typeof window !== "undefined") {
        const p = new URLSearchParams(window.location.search);
        p.set("audit", "precedents");
        p.set("case", caseId);
        const next = "?" + p.toString();
        if (next !== window.location.search) {
          window.history.pushState({}, "", next);
        }
      }
    },
    [fetchDetail, inlineMode],
  );

  // ── 전체목록(그리드 복귀) ───────────────────────────────────
  const backToList = useCallback(() => {
    setSelectedCaseId(null);
    setDetail(null);
    setDetailErr(null);
    if (!inlineMode && typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search);
      p.delete("case");
      const next = "?" + p.toString();
      if (next !== window.location.search) {
        window.history.pushState({}, "", next);
      }
    }
  }, [inlineMode]);

  // ── 소스 토글 ───────────────────────────────────────────────
  const switchSource = useCallback((src) => {
    setSource(src);
    setFeedMode("recent");
    setSearched(false);
    setInput("");
    setPage(1);
    setTotal(null);
    setFeedMaxDate(null);
  }, []);

  // recent 목록 로드 — source/page 변경 시(검색 모드 아닐 때만).
  useEffect(() => {
    if (feedMode === "recent") fetchRecent(source, page);
  }, [source, page, feedMode, fetchRecent]);

  // 검색결과에서 진입한 딥링크는 동일 검색어의 사례 목록도 함께 복원한다.
  useEffect(() => {
    if (inlineMode) runSearch(normalizedInlineKeyword);
    else if (initialNavigation.originQuery) runSearch(initialNavigation.originQuery);
    // 최초 URL 컨텍스트만 적용. 이후 URL 변경은 아래 popstate에서 동기화한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 마운트: 딥링크 case= 있으면 상세 직접 로드.
  useEffect(() => {
    const c = initialNavigation.caseId;
    if (c) {
      fetchDetail(c);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // popstate: 뒤/앞으로 가기 시 case 동기화.
  useEffect(() => {
    if (inlineMode) return undefined;
    const onPop = () => {
      const nextNavigation = readPrecedentNavigation(window.location.search);
      setNavigation(nextNavigation);
      const c = nextNavigation.caseId;
      if (
        nextNavigation.originQuery &&
        (nextNavigation.originQuery !== originQueryRef.current ||
          nextNavigation.originSource !== originSourceRef.current)
      ) {
        originQueryRef.current = nextNavigation.originQuery;
        originSourceRef.current = nextNavigation.originSource;
        setSource(nextNavigation.originSource);
        runSearch(nextNavigation.originQuery);
      }
      if (c) {
        setSelectedCaseId((prev) => {
          if (prev !== c) fetchDetail(c);
          return c;
        });
      } else {
        setSelectedCaseId(null);
        setDetail(null);
        setDetailErr(null);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [fetchDetail, inlineMode, runSearch]);

  // 세번 상세로 이동(결정세번 클릭).
  const goToHeading = useCallback((hsk) => {
    const d = String(hsk == null ? "" : hsk).replace(/[^0-9]/g, "");
    if (d.length < 4) return;
    const p = new URLSearchParams();
    p.set("audit", "onto-detail");
    p.set("hs", d.slice(0, 4));
    if (d.length >= 10) p.set("hsk", d.slice(0, 10));
    window.history.pushState({}, "", "/?" + p.toString());
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, []);

  const onKeyDown = (e) => {
    if (
      e.key === "Enter" &&
      !composingRef.current &&
      !e.nativeEvent?.isComposing
    ) {
      runSearch();
    }
  };

  const detailMode = !!selectedCaseId;
  const isSaved = viewTab === "saved";
  const isSearch = !isSaved && feedMode === "search";
  const returnTarget = normalizeReturnTo(navigation.returnTo);
  const returnToOrigin = useCallback(() => {
    if (navigateToReturnTarget(navigation.returnTo)) return;
    backToList();
  }, [backToList, navigation.returnTo]);

  // 표시용 페이지 항목 — 전체목록(recent/search) vs 내 사례철(saved)
  const searchItems = isSearch
    ? items.filter((it) =>
        source === "foreign" ? isForeignCase(it) : !isForeignCase(it),
      )
    : items;
  const searchTotalPages = Math.max(1, Math.ceil(searchItems.length / PAGE_SIZE));
  const curSearchPage = Math.min(searchPage, searchTotalPages);
  const allPageItems = isSearch
    ? searchItems.slice((curSearchPage - 1) * PAGE_SIZE, curSearchPage * PAGE_SIZE)
    : items;

  const savedItems = sc.items;
  const savedTotalPages = Math.max(1, Math.ceil(savedItems.length / PAGE_SIZE));
  const curSavedPage = Math.min(savedPage, savedTotalPages);
  const savedPageItems = savedItems.slice(
    (curSavedPage - 1) * PAGE_SIZE,
    curSavedPage * PAGE_SIZE,
  );

  // 그리드 본문 + 상세 좌측 리스트가 함께 쓰는 활성 항목.
  const gridItems = isSaved ? savedPageItems : allPageItems;
  // 이미지 세번에서 직접 들어온 분할 화면은 페이지를 나누지 않고
  // 서버가 반환한 해당 세번 사례(최대 200건)를 좌측에 모두 표시한다.
  const detailListItems = navigation.splitView && isSearch ? searchItems : gridItems;
  const splitAutoCaseId =
    navigation.splitView &&
    isSearch &&
    !listLoading &&
    !listErr &&
    !selectedCaseId
      ? detailListItems.find((it) => it && it.case_id)?.case_id || null
      : null;

  // 이미지 아래 세번 클릭은 곧 해당 세번 사례 확인 의도이므로
  // 별도 그리드를 거치지 않고 첫 사례의 좌목록·우상세 화면을 연다.
  useEffect(() => {
    if (splitAutoCaseId) selectCase(splitAutoCaseId);
  }, [selectCase, splitAutoCaseId]);

  // 이미지에서 특정 사례로 들어온 경우 좌측 목록도 그 사례가 보이는 위치로 맞춘다.
  useEffect(() => {
    if (!inlineMode || !selectedCaseId || !listScrollRef.current) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const container = listScrollRef.current;
      if (!container) return;
      const row = Array.from(container.querySelectorAll("[data-case-id]")).find(
        (element) => element.dataset.caseId === selectedCaseId,
      );
      if (!row) return;
      const containerRect = container.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      if (rowRect.top < containerRect.top || rowRect.bottom > containerRect.bottom) {
        container.scrollTop += rowRect.top - containerRect.top - 8;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [detailListItems.length, inlineMode, selectedCaseId]);

  const contextualSplit = !!(
    navigation.splitView &&
    isSearch &&
    (returnTarget || inlineMode)
  );
  const inlineWaiting = inlineMode && !detailMode;

  const newCutoff =
    !isSaved && !isSearch && feedMaxDate
      ? isoMinusDays(feedMaxDate, NEW_DAYS)
      : null;
  const isNew = (d) => !!(d && newCutoff && ymd(d) >= newCutoff);

  const totalPages = total ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : null;
  const liveMode = detectMode(input);
  const placeholder = "세번·품명·결정내용 검색 (예: 8471 · 핸드백 · 무화과)";

  // ── 검색바(그리드·상세 공통) ────────────────────────────────
  const searchBar = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {/* 국내/해외 소스 토글 */}
        <div
          style={{
            display: "inline-flex",
            background: "var(--audit-bg-hover)",
            borderRadius: 6,
            padding: 2,
          }}
        >
          {[
            { id: "domestic", label: "국내" },
            { id: "foreign", label: "해외" },
          ].map((s) => {
            const active = source === s.id;
            return (
              <button
                key={s.id}
                onClick={() => switchSource(s.id)}
                title={
                  s.id === "domestic"
                    ? "관세청 품목분류위원회·CLIP·사전심사"
                    : "외국 관세당국 결정례"
                }
                style={{
                  padding: "4px 16px",
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  background: active ? "var(--audit-bg-elevated)" : "transparent",
                  color: active ? "var(--audit-text)" : "var(--audit-text-muted)",
                  boxShadow: active ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        <span
          style={{
            fontSize: 11.5,
            color: "var(--audit-text-faint)",
            whiteSpace: "nowrap",
          }}
        >
          숫자는 세번(HSK), 그 외는 품명·내용 자동 검색
        </span>
      </div>

      <label
        htmlFor="precedent-database-search"
        style={{
          fontSize: 12.5,
          lineHeight: "18px",
          fontWeight: 700,
          color: "var(--audit-text)",
        }}
      >
        사례 DB 내부 검색
      </label>

      <div style={{ display: "flex", gap: 6 }}>
        <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
          <Search
            size={13}
            style={{
              position: "absolute",
              left: 8,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--audit-text-muted)",
            }}
          />
          <input
            id="precedent-database-search"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            onCompositionStart={() => {
              composingRef.current = true;
            }}
            onCompositionEnd={() => {
              composingRef.current = false;
            }}
            placeholder={placeholder}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "7px 10px 7px 26px",
              fontSize: 13,
              border: "1px solid var(--audit-border-strong)",
              background: "var(--audit-bg)",
              color: "var(--audit-text)",
              borderRadius: 6,
              fontFamily:
                liveMode === "hsk" ? "JetBrains Mono, monospace" : "inherit",
            }}
          />
        </div>
        <button
          onClick={runSearch}
          className="audit-btn audit-btn-primary"
          style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap" }}
        >
          검색
        </button>
        {isSearch && (
          <button
            onClick={() => {
              setInput("");
              setFeedMode("recent");
              setSearched(false);
              setPage(1);
            }}
            className="audit-btn"
            style={{ whiteSpace: "nowrap" }}
            title="검색 해제 · 전체목록"
          >
            전체
          </button>
        )}
      </div>

      {/* 건수 */}
      <div
        style={{
          fontSize: 12,
          color: "var(--audit-text-muted)",
          fontFamily: "JetBrains Mono, monospace",
          minHeight: 16,
        }}
      >
        {listLoading
          ? "불러오는 중…"
          : listErr
            ? ""
            : isSearch
              ? searched
                ? `검색 ${searchItems.length.toLocaleString()}건${items.length >= SEARCH_LIMIT ? " (상위 200)" : ""}`
                : ""
              : total != null
                ? `${source === "foreign" ? "해외" : "국내"} 총 ${total.toLocaleString()}건 · 시행일순`
                : ""}
      </div>
    </div>
  );

  // ── 목록 상태 렌더 헬퍼 ─────────────────────────────────────
  const listStatus = () => {
    // recent/search 로딩·에러는 전체목록 뷰에서만(내 사례철은 스토어 기반).
    if (!isSaved && listLoading)
      return (
        <div style={{ padding: 16, fontSize: 13, lineHeight: "19px", color: "var(--audit-text-muted)" }}>
          불러오는 중…
        </div>
      );
    if (!isSaved && listErr)
      return (
        <div style={{ padding: 16, fontSize: 13, lineHeight: "19px" }}>
          <div style={{ color: "var(--audit-text-muted)", marginBottom: 8 }}>
            불러오지 못했습니다 · {listErr}
          </div>
          <button
            className="audit-btn"
            onClick={() =>
              isSearch ? runSearch() : fetchRecent(source, page)
            }
          >
            재시도
          </button>
        </div>
      );
    if (gridItems.length === 0)
      return (
        <div
          style={{
            padding: 20,
            fontSize: 13,
            color: "var(--audit-text-muted)",
            lineHeight: 1.7,
          }}
        >
          {isSaved
            ? sc.loggedIn
              ? "담은 사례가 없습니다. 사례 상세에서 [☆ 내 사례철에 담기] 로 저장하세요."
              : "로그인 후 이용할 수 있습니다."
            : isSearch
              ? "검색 결과가 없습니다."
              : "표시할 사례가 없습니다."}
        </div>
      );
    return null;
  };

  // ── 페이지네이션 푸터 (전체목록 recent/search · 내 사례철) ────
  const paginationFooter = (() => {
    if (isSaved) {
      if (savedItems.length <= PAGE_SIZE) return null;
      return (
        <PageNav
          cur={curSavedPage}
          totalPages={savedTotalPages}
          canPrev={curSavedPage > 1}
          canNext={curSavedPage < savedTotalPages}
          onPrev={() => setSavedPage((p) => Math.max(1, p - 1))}
          onNext={() => setSavedPage((p) => Math.min(savedTotalPages, p + 1))}
        />
      );
    }
    if (listLoading || listErr) return null;
    if (isSearch) {
      if (items.length <= PAGE_SIZE) return null;
      return (
        <PageNav
          cur={curSearchPage}
          totalPages={searchTotalPages}
          canPrev={curSearchPage > 1}
          canNext={curSearchPage < searchTotalPages}
          onPrev={() => setSearchPage((p) => Math.max(1, p - 1))}
          onNext={() => setSearchPage((p) => Math.min(searchTotalPages, p + 1))}
        />
      );
    }
    if (page <= 1 && !hasMore) return null;
    return (
      <PageNav
        cur={page}
        totalPages={totalPages}
        canPrev={page > 1}
        canNext={hasMore}
        onPrev={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => p + 1)}
      />
    );
  })();

  return (
    <div
      className="audit-root"
      style={{
        height: inlineMode
          ? "min(720px, calc(100vh - 180px))"
          : embedded
            ? "100%"
            : "100vh",
        display: "flex",
        flexDirection: "column",
        background: "var(--audit-bg)",
        color: "var(--audit-text)",
        fontFamily: "inherit",
        minHeight: inlineMode ? 520 : 0,
        overflow: inlineMode ? "hidden" : undefined,
      }}
    >
      {inlineWaiting ? (
        <div
          style={{
            minHeight: 260,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            padding: 24,
            color: "var(--audit-text-muted)",
            fontSize: 13,
            lineHeight: "19px",
          }}
        >
          {listLoading ? (
            "해당 세번의 품목분류사례를 불러오는 중…"
          ) : listErr ? (
            <>
              <span>품목분류사례를 불러오지 못했습니다 · {listErr}</span>
              <button
                type="button"
                className="audit-btn"
                onClick={() => runSearch(normalizedInlineQuery)}
              >
                재시도
              </button>
            </>
          ) : (
            "이 세번에 해당하는 품목분류사례가 없습니다."
          )}
        </div>
      ) : detailMode ? (
        // ══════════ 상세 모드: 좌 리스트 + 우 상세 ══════════
        <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
          {/* 좌: 세번·품명 리스트 */}
          <div
            style={{
              width: "clamp(300px, 25vw, 324px)",
              flexShrink: 0,
              borderRight: "1px solid var(--audit-border)",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <div
              style={{
                padding: "8px 10px",
                borderBottom: "1px solid var(--audit-border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 700 }}>
                {contextualSplit
                  ? `${formatHsk(navigation.originQuery)} 사례 ${detailListItems.length.toLocaleString()}건${items.length >= SEARCH_LIMIT ? " · 상위 200" : ""}`
                  : navigation.originQuery
                    ? `“${navigation.originQuery}” 검색 사례`
                    : "품목분류사례"}
              </span>
              {!inlineMode && (
                <button
                  onClick={contextualSplit ? returnToOrigin : backToList}
                  className="audit-btn"
                  style={{ padding: "3px 8px", fontSize: 12, whiteSpace: "nowrap" }}
                  title={
                    contextualSplit
                      ? "이미지에서 세번을 선택했던 이전 화면으로 돌아가기"
                      : isSearch
                        ? `“${navigation.originQuery || input}” 검색결과 목록으로 돌아가기`
                        : "품목분류사례 전체목록으로 돌아가기"
                  }
                >
                  {contextualSplit
                    ? "← 이전 화면"
                    : isSearch
                      ? "← 검색결과"
                      : "← 전체목록"}
                </button>
              )}
            </div>
            <div ref={listScrollRef} style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {detailListItems.map((it, i) => {
                const cid = it.case_id;
                const active = cid && cid === selectedCaseId;
                return (
                  <button
                    key={cid || i}
                    data-case-id={cid || undefined}
                    className={`precedent-case-row${active ? " is-active" : ""}`}
                    onClick={() => selectCase(cid)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "7px 10px",
                      border: "none",
                      borderBottom: "1px solid var(--audit-border)",
                      borderLeft: active
                        ? "3px solid var(--audit-accent)"
                        : "3px solid transparent",
                      background: active ? "#f1f3f5" : "transparent",
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                    onMouseEnter={(e) => {
                      if (!active)
                        e.currentTarget.style.background = "var(--audit-bg-subtle)";
                    }}
                    onMouseLeave={(e) => {
                      if (!active) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        minHeight: 18,
                      }}
                    >
                      <span
                        className="precedent-case-hsk"
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          fontFamily: "JetBrains Mono, monospace",
                          color: "#111827",
                        }}
                      >
                        {formatHsk(it.hsk || it.hs6) || "—"}
                      </span>
                      <SourceBadge foreign={isForeignCase(it)} />
                      {ymd(it.decision_date) && (
                        <div
                          style={{
                            marginLeft: "auto",
                            fontSize: 11.5,
                            color: "var(--audit-text-faint)",
                            fontFamily: "JetBrains Mono, monospace",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ymd(it.decision_date)}
                        </div>
                      )}
                    </div>
                    <PrecedentIdentity item={it} />
                    <div
                      style={{
                        marginTop: 3,
                        fontSize: 13,
                        color: "var(--audit-text)",
                        lineHeight: "18px",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {highlightText(
                        it.title || it.label || "(품명 없음)",
                        isSearch && searchMode === "name" ? input : "",
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 우: 상세 + 액션바 */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              <DetailPane
                caseId={selectedCaseId}
                detail={detail}
                loading={detailLoading}
                err={detailErr}
                onRetry={() => fetchDetail(selectedCaseId)}
                onHeadingClick={goToHeading}
              />
            </div>
            {detail && !detailLoading && (
              <DetailActionBar
                detail={detail}
                onBackToList={contextualSplit ? returnToOrigin : backToList}
                showBack={!inlineMode}
                backTitle={
                  contextualSplit
                    ? "이미지에서 세번을 선택했던 이전 화면으로 돌아가기"
                    : "현재 사례를 열기 전 목록으로 돌아가기"
                }
              />
            )}
          </div>
        </div>
      ) : (
        // ══════════ 그리드 모드 ══════════
        <>
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--audit-border)",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {returnTarget && (
                  <button
                    type="button"
                    onClick={returnToOrigin}
                    className="audit-btn"
                    title="세번을 선택했던 이전 화면으로 돌아가기"
                  >
                    ← 이전 화면
                  </button>
                )}
                <h1 style={{ fontSize: 15, margin: 0, fontWeight: 700 }}>
                  {isSaved ? "내 사례철" : "품목분류사례 검색"}
                </h1>
              </div>
              {/* 뷰 토글 — 전체목록 | 내 사례철 */}
              <div
                style={{
                  display: "inline-flex",
                  background: "var(--audit-bg-hover)",
                  borderRadius: 6,
                  padding: 2,
                }}
              >
                {[
                  { id: "all", label: "전체목록" },
                  {
                    id: "saved",
                    label: `내 사례철${sc.count ? ` ${sc.count}` : ""}`,
                  },
                ].map((v) => {
                  const active = viewTab === v.id;
                  return (
                    <button
                      key={v.id}
                      onClick={() => {
                        setViewTab(v.id);
                        setSavedPage(1);
                      }}
                      style={{
                        padding: "4px 14px",
                        fontSize: 12.5,
                        fontWeight: active ? 700 : 500,
                        border: "none",
                        borderRadius: 4,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        background: active
                          ? "var(--audit-bg-elevated)"
                          : "transparent",
                        color: active
                          ? "var(--audit-text)"
                          : "var(--audit-text-muted)",
                        boxShadow: active ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                      }}
                    >
                      {v.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* 검색·소스 토글은 전체목록 뷰에서만 */}
            {!isSaved && searchBar}
          </div>

          <div ref={listScrollRef} style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
            {listStatus() || (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                  tableLayout: "fixed",
                }}
              >
                <colgroup>
                  <col style={{ width: 128 }} />
                  <col />
                  <col style={{ width: 170 }} />
                  <col style={{ width: 108 }} />
                  {isSaved && <col style={{ width: 52 }} />}
                </colgroup>
                <thead>
                  <tr>
                    <GridTh>HSK</GridTh>
                    <GridTh align="left">품명</GridTh>
                    <GridTh>구분</GridTh>
                    <GridTh>시행일</GridTh>
                    {isSaved && <GridTh> </GridTh>}
                  </tr>
                </thead>
                <tbody>
                  {gridItems.map((it, i) => {
                    const cid = it.case_id;
                    return (
                      <tr
                        key={cid || i}
                        onClick={() => selectCase(cid)}
                        style={{ cursor: "pointer" }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "var(--audit-bg-subtle)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        <td style={gridTdMono}>{formatHsk(it.hsk || it.hs6) || "—"}</td>
                        <td style={gridTdName}>
                          <span
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {highlightText(
                              it.title || it.label || "(품명 없음)",
                              isSearch && searchMode === "name" ? input : "",
                            )}
                          </span>
                          {isSearch &&
                            searchMode === "name" &&
                            input.trim() &&
                            !titleHasQuery(it.title || it.label, input) &&
                            (it.match_snippet ? (
                              <BodyMatchSnippet
                                snippet={it.match_snippet}
                                query={input}
                              />
                            ) : (
                              <BodyMatchBadge />
                            ))}
                          {isNew(it.decision_date) && <NewBadge />}
                          {isSaved && it.note && (
                            <div
                              style={{
                                fontSize: 11.5,
                                color: "var(--audit-text-faint)",
                                fontStyle: "italic",
                                marginTop: 3,
                              }}
                            >
                              ✎ {it.note}
                            </div>
                          )}
                        </td>
                        <td style={gridTdGubun}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                            <SourceBadge foreign={isForeignCase(it)} />
                            <span>{deriveGubun(it)}</span>
                          </span>
                        </td>
                        <td style={gridTdDate}>{ymd(it.decision_date) || "—"}</td>
                        {isSaved && (
                          <td style={{ ...gridTdBase, textAlign: "center" }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                sc.toggle(cid, {});
                              }}
                              title="내 사례철에서 제거"
                              style={{
                                border: "none",
                                background: "transparent",
                                color: "var(--audit-text-muted)",
                                cursor: "pointer",
                                fontSize: 13,
                                padding: "2px 6px",
                                borderRadius: 4,
                              }}
                            >
                              ✕
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {paginationFooter}
        </>
      )}
    </div>
  );
}

// ── 페이지네이션 컨트롤 ─────────────────────────────────────
function PageNav({ cur, totalPages, canPrev, canNext, onPrev, onNext }) {
  return (
    <div
      style={{
        padding: "8px 14px",
        borderTop: "1px solid var(--audit-border)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        fontSize: 12,
        color: "var(--audit-text-muted)",
        background: "var(--audit-bg)",
        flexShrink: 0,
      }}
    >
      <button
        onClick={onPrev}
        disabled={!canPrev}
        className="audit-btn"
        style={{
          padding: "2px 10px",
          opacity: canPrev ? 1 : 0.4,
          cursor: canPrev ? "pointer" : "default",
        }}
      >
        ‹ 이전
      </button>
      <span style={{ fontFamily: "JetBrains Mono, monospace" }}>
        {cur}
        {totalPages ? ` / ${totalPages}` : ""}
      </span>
      <button
        onClick={onNext}
        disabled={!canNext}
        className="audit-btn"
        style={{
          padding: "2px 10px",
          opacity: canNext ? 1 : 0.4,
          cursor: canNext ? "pointer" : "default",
        }}
      >
        다음 ›
      </button>
    </div>
  );
}

function NewBadge() {
  return (
    <span
      style={{
        marginLeft: 6,
        fontSize: 11.5,
        fontWeight: 700,
        color: "#fff",
        background: "var(--audit-accent)",
        borderRadius: 3,
        padding: "1px 4px",
        verticalAlign: "middle",
        letterSpacing: "0.04em",
      }}
    >
      NEW
    </span>
  );
}

// ── 하단 액션바(내 품목·내 사례철·인쇄하기·이전 화면) ──────────
function DetailActionBar({ detail, onBackToList, backTitle, showBack = true }) {
  const wl = useWatchlist();
  const sc = useSavedCases();
  const code = String(detail.hsk || "").replace(/\D/g, "");
  const savedWatch = code ? wl.has(code) : false;
  const caseId = detail.case_id;
  const inArchive = caseId ? sc.has(caseId) : false;
  const archived = inArchive
    ? sc.items.find((i) => i.case_id === caseId)
    : null;

  const [note, setNote] = useState("");
  const [noteDirty, setNoteDirty] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  // case/담김 상태 바뀌면 메모 동기화.
  useEffect(() => {
    setNote(archived?.note || "");
    setNoteDirty(false);
    setNoteSaved(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, inArchive]);

  const meta = {
    hsk: code,
    title: detail.title || "",
    source: detail.source || "",
    decision_date: ymd(detail.decision_date) || "",
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: "7px 12px",
        borderTop: "1px solid var(--audit-border)",
        background: "var(--audit-bg)",
        flexShrink: 0,
      }}
    >
      {/* 메모 — 내 사례철에 담긴 경우만 */}
      {inArchive && (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span
            style={{ fontSize: 12, color: "var(--audit-text-muted)", flexShrink: 0 }}
          >
            메모
          </span>
          <input
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              setNoteDirty(true);
              setNoteSaved(false);
            }}
            maxLength={1000}
            placeholder="예: 2024 수입건 동일물품 근거 (선택)"
            style={{
              flex: 1,
              minWidth: 0,
              padding: "6px 10px",
              fontSize: 12.5,
              border: "1px solid var(--audit-border-strong)",
              background: "var(--audit-bg)",
              color: "var(--audit-text)",
              borderRadius: 6,
              fontFamily: "inherit",
            }}
          />
          <button
            className="audit-btn"
            disabled={!noteDirty}
            onClick={() =>
              sc.saveNote(caseId, note).then((ok) => {
                if (ok) {
                  setNoteDirty(false);
                  setNoteSaved(true);
                }
              })
            }
            style={{ opacity: noteDirty ? 1 : 0.5, whiteSpace: "nowrap" }}
          >
            {noteSaved && !noteDirty ? "✓ 저장됨" : "메모 저장"}
          </button>
        </div>
      )}
      {/* 버튼 줄 */}
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <button
          onClick={() => {
            if (!code) return;
            if (!wl.loggedIn) {
              alert("로그인 후 이용할 수 있습니다.");
              return;
            }
            wl.toggle(code, detail.title || "");
          }}
          disabled={!code}
          className={savedWatch ? "audit-btn audit-btn-primary" : "audit-btn"}
          title={
            code
              ? savedWatch
                ? "세번 즐겨찾기에서 제거"
                : "이 세번을 즐겨찾기에 저장"
              : "세번 정보 없음"
          }
          style={{ opacity: code ? 1 : 0.5, padding: "5px 10px", fontSize: 12.5 }}
        >
          {savedWatch ? "★ 세번 즐겨찾기" : "☆ 즐겨찾기"}
        </button>
        <button
          onClick={() => {
            if (!caseId) return;
            if (!sc.loggedIn) {
              alert("로그인 후 이용할 수 있습니다.");
              return;
            }
            sc.toggle(caseId, meta);
          }}
          disabled={!caseId}
          className={inArchive ? "audit-btn audit-btn-primary" : "audit-btn"}
          title={inArchive ? "내 사례철에서 제거" : "이 사례를 내 사례철에 담기"}
          style={{ opacity: caseId ? 1 : 0.5, padding: "5px 10px", fontSize: 12.5 }}
        >
          {inArchive ? "★ 내 사례철" : "☆ 내 사례철"}
        </button>
        <button
          onClick={() => printDetail(detail)}
          className="audit-btn"
          style={{ padding: "5px 10px", fontSize: 12.5 }}
        >
          인쇄하기
        </button>
        {showBack && (
          <button
            onClick={onBackToList}
            className="audit-btn"
            title={backTitle || "현재 사례를 열기 전 목록으로 돌아가기"}
            style={{ padding: "5px 10px", fontSize: 12.5 }}
          >
            ← 이전 화면
          </button>
        )}
      </div>
    </div>
  );
}

// ── 우측 상세 카드 ──────────────────────────────────────────
function DetailPane({ caseId, detail, loading, err, onRetry, onHeadingClick }) {
  if (!caseId) {
    return (
      <Centered>
        <span style={{ color: "var(--audit-text-muted)", fontSize: 13 }}>
          왼쪽에서 사례를 선택하세요
        </span>
      </Centered>
    );
  }
  if (loading) {
    return (
      <Centered>
        <span style={{ color: "var(--audit-text-muted)", fontSize: 13 }}>
          불러오는 중…
        </span>
      </Centered>
    );
  }
  if (err || !detail) {
    return (
      <Centered>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              color: "var(--audit-text-muted)",
              fontSize: 13,
              marginBottom: 10,
            }}
          >
            상세를 불러오지 못했습니다 · 재시도
          </div>
          <button className="audit-btn" onClick={onRetry}>
            재시도
          </button>
        </div>
      </Centered>
    );
  }

  const hsk = detail.hsk || "";
  const images = Array.isArray(detail.images) ? detail.images : [];
  const isFull = detail.full !== false;
  const amendmentIntegrity = detail.amendment?.integrity_status || "unresolved";
  const amendmentMatched = amendmentIntegrity === "matched" && detail.amendment?.hs_after;
  const amendmentRevoked = amendmentIntegrity === "revoked";
  const summaryFacts = [
    { label: "시행기관", value: detail.org },
    // 해외사례는 결정국의 세번 표기(예: 3926.90.39 AHTN 8자리)를 그대로 보이고, 우리 6자리는 검색 연결에만 쓴다.
    { label: "결정세번", value: detail.decided_code_foreign || hsk, mono: true, heading: true },
    {
      label: "출처",
      value: [detail.source, detail.country].filter(Boolean).join(" · "),
    },
  ].filter((fact) => fact.value);

  return (
    <div style={{ padding: "8px 14px 12px", maxWidth: 1080 }}>
      {/* 헤더 */}
      <div
        data-testid="precedent-detail-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "nowrap",
          position: "sticky",
          top: 0,
          zIndex: 2,
          background: "var(--audit-bg)",
          minHeight: 34,
          paddingTop: 0,
          paddingBottom: 5,
          borderBottom: "1px solid var(--audit-border-strong)",
          marginBottom: 8,
        }}
      >
        <button
          className="precedent-detail-hsk"
          onClick={() => onHeadingClick(hsk)}
          title="세번 상세로 이동"
          style={{
            fontFamily: "JetBrains Mono, monospace",
            fontSize: 18,
            fontWeight: 700,
            color: "#111827",
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: "pointer",
            textDecoration: "none",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.textDecoration = "underline")
          }
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
        >
          {detail.decided_code_foreign || formatHsk(hsk)}
        </button>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 8,
            minWidth: 0,
            fontSize: 12,
            color: "var(--audit-text-muted)",
            fontFamily: "JetBrains Mono, monospace",
          }}
        >
          {detail.ref_no && (
            <span style={{ whiteSpace: "nowrap" }}>{detail.ref_no}</span>
          )}
          {ymd(detail.decision_date) && (
            <>
              <span aria-hidden="true" style={{ color: "var(--audit-border-strong)" }}>·</span>
              <span style={{ whiteSpace: "nowrap" }}>{ymd(detail.decision_date)}</span>
            </>
          )}
          <SourceEvidenceLink
            compact
            {...precedentEvidence({
              decisionUrl: detail.decision_url,
              caseId: detail.case_id,
              source: detail.source,
              code: hsk,
              refNo: detail.ref_no,
              officialForeignSearch: true,
              officialUrl: detail.official_url,
            })}
          />
        </div>
      </div>

      {/* 발췌 배지 */}
      {!isFull && (
        <div
          style={{
            display: "inline-block",
            fontSize: 12,
            color: "var(--audit-text-muted)",
            background: "var(--audit-bg-subtle)",
            border: "1px solid var(--audit-border)",
            borderRadius: 10,
            padding: "2px 8px",
            marginBottom: 8,
          }}
        >
          전문 준비 중 — 발췌 표시
        </div>
      )}

      {/* 변경고시 배너(T3) — 폐기된 결정은 원본 그대로 두고 "현재 답"을 최상단에. 연혁도 상품. */}
      {detail.status === "superseded" && detail.amendment && (
        <section
          style={{
            marginBottom: 8,
            padding: "10px 14px",
            border: `1px solid ${amendmentIntegrity === "unresolved" ? "#ef9a9a" : "#f0c36d"}`,
            borderLeft: `4px solid ${amendmentIntegrity === "unresolved" ? "#d93025" : "#b06000"}`,
            borderRadius: 8,
            background: "#fef7e0",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: amendmentIntegrity === "unresolved" ? "#d93025" : "#b06000" }}>
              {amendmentIntegrity === "unresolved"
                ? "변경고시 연결 검증이 필요합니다 — 변경후 세번 자동 표시를 중지했습니다"
                : "이 결정은 품목분류 변경고시로 변경되었습니다"}
              {detail.amendment.gosi_no ? ` — 제${detail.amendment.gosi_no}호` : ""}
              {detail.amendment.gosi_date ? ` (시행 ${detail.amendment.gosi_date})` : ""}
            </div>
            <AmendmentOriginalButton amendment={detail.amendment} hsk={detail.hsk} />
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "#5f6368" }}>변경 후 세번</span>
            {amendmentMatched ? (
              <button
                onClick={() => onHeadingClick(String(detail.amendment.hs_after).replace(/\D/g, ""))}
                title="변경 후 세번 상세로 이동"
                style={{
                  fontFamily: "JetBrains Mono, monospace",
                  fontSize: 17,
                  fontWeight: 750,
                  color: "#b06000",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                {detail.amendment.hs_after}
              </button>
            ) : amendmentRevoked ? (
              <span style={{ fontSize: 13, color: "#5f6368" }}>결정 폐지(대체 세번 없음 — 고시 원문 참조)</span>
            ) : (
              <span style={{ fontSize: 13, fontWeight: 700, color: "#d93025" }}>
                원문과 종전세번의 연결을 확인하기 전에는 표시하지 않습니다
              </span>
            )}
          </div>
          <div style={{ fontSize: 11.5, lineHeight: "16px", color: "#70757a", marginTop: 6 }}>
            변경일부터 30일 내 선적분은 신고인에게 유리한 경우 변경 전 분류를 적용할 수 있습니다(관세법 제87조).
          </div>
        </section>
      )}

      <section
        style={{
          marginBottom: 8,
          padding: "10px 14px",
          border: "1px solid var(--audit-border)",
          borderLeft: "3px solid var(--audit-text)",
          borderRadius: 6,
          background: "var(--audit-bg-subtle)",
        }}
      >
        <div style={{ fontSize: 11.5, color: "var(--audit-text-muted)", marginBottom: 3 }}>품목분류 결정</div>
        <div style={{ fontSize: 15, fontWeight: 750, color: "var(--audit-text)", lineHeight: 1.4 }}>{detail.title || "품명 확인 필요"}</div>
        {detail.title_original && detail.title_original !== detail.title && (
          <div data-testid="precedent-title-original" style={{ marginTop: 4, fontSize: 13, color: "var(--audit-text-muted)", lineHeight: 1.4 }}>
            원문 품명: {detail.title_original}
          </div>
        )}
      </section>

      {summaryFacts.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            columnGap: 18,
            marginBottom: 8,
            borderTop: "1px solid var(--audit-border)",
          }}
        >
          {summaryFacts.map((fact) => (
            <div
              key={fact.label}
              style={{
                minHeight: 30,
                display: "grid",
                gridTemplateColumns: "76px minmax(0, 1fr)",
                alignItems: "center",
                gap: 8,
                borderBottom: "1px solid var(--audit-border)",
                fontSize: 12.5,
              }}
            >
              <span style={{ color: "var(--audit-text-muted)", fontWeight: 600 }}>
                {fact.label}
              </span>
              {fact.heading ? (
                <button
                  onClick={() => onHeadingClick(hsk)}
                  title="세번 상세로 이동"
                  style={{
                    width: "fit-content",
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    color: "var(--audit-text)",
                    cursor: "pointer",
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: 12.5,
                    fontWeight: 700,
                  }}
                >
                  {fact.value || formatHsk(hsk)}
                </button>
              ) : (
                <span
                  style={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "var(--audit-text)",
                    fontFamily: fact.mono ? "JetBrains Mono, monospace" : "inherit",
                  }}
                  title={String(fact.value)}
                >
                  {fact.value}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 이미지·본문 표 */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 13,
          tableLayout: "fixed",
        }}
      >
        <tbody>
          {/* 관련 이미지 */}
          {images.length > 0 && (
            <tr>
              <Th>관련이미지</Th>
              <td style={tdStyle}>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--audit-text-muted)",
                    marginBottom: 4,
                  }}
                >
                  이미지 {images.length}건
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {images.map((aid, k) => (
                    <a
                      key={`${aid}-${k}`}
                      href={CLIP_IMG_BASE + encodeURIComponent(aid)}
                      target="_blank"
                      rel="noreferrer"
                      title="원본 이미지 열기 ↗"
                    >
                      <img
                        src={CLIP_IMG_BASE + encodeURIComponent(aid)}
                        alt={detail.title || caseId}
                        loading="lazy"
                        style={{
                          width: 104,
                          height: 104,
                          objectFit: "cover",
                          border: "1px solid var(--audit-border)",
                          borderRadius: 4,
                          background: "var(--audit-bg-subtle)",
                        }}
                      />
                    </a>
                  ))}
                </div>
              </td>
            </tr>
          )}
          {/* 물품설명 */}
          <tr>
            <Th>물품설명</Th>
            <td style={tdStyle}>
              {detail.source === "clip_foreign" ? (
                <ForeignTextCard
                  testId="foreign-description"
                  title="분류 대상 물품의 확인 사실"
                  original={detail.description_original}
                  korean={detail.description_ko_full}
                  fallback={detail.description}
                />
              ) : (
                <DetailTextCard title="분류 대상 물품의 확인 사실">
                  <RulingParagraphs text={detail.description} />
                </DetailTextCard>
              )}
            </td>
          </tr>
          {/* 결정사유 */}
          {detail.status === "superseded" && (
            <tr>
              <Th>변경 후 결정사유</Th>
              <td style={tdStyle}><AmendmentReason amendment={detail.amendment} hsk={detail.hsk} /></td>
            </tr>
          )}
          <tr>
            <Th>{detail.status === "superseded" ? "변경 전 결정사유" : "결정사유"}</Th>
            <td style={tdStyle}>
              {detail.source === "clip_foreign" && (detail.reason_original || detail.reason_ko_full) ? (
                <ForeignTextCard
                  testId="foreign-reason"
                  title="세번 결정 논리"
                  original={detail.reason_original}
                  korean={detail.reason_ko_full}
                  fallback={detail.reason}
                  emphasized
                />
              ) : (
                <DetailTextCard title={detail.status === "superseded" ? `당시 결정사유 · ${formatHsk(hsk)} (변경 전)` : "세번 결정 논리"} emphasized={detail.status !== "superseded"}>
                  <RulingParagraphs text={detail.reason} />
                </DetailTextCard>
              )}
            </td>
          </tr>
        </tbody>
      </table>

      {/* 원문 근거 — 상세 말미에도 유지해 긴 본문을 읽은 뒤 바로 확인 가능. */}
      {(detail.decision_url || detail.case_id || hsk) && (
        <div style={{ marginTop: 10 }}>
          <SourceEvidenceLink
            {...precedentEvidence({
              decisionUrl: detail.decision_url,
              caseId: detail.case_id,
              source: detail.source,
              code: hsk,
              refNo: detail.ref_no,
              officialForeignSearch: true,
              officialUrl: detail.official_url,
            })}
          />
        </div>
      )}
    </div>
  );
}

// ── 그리드 셀 스타일 ────────────────────────────────────────
const gridThBase = {
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: "var(--audit-bg-subtle)",
  padding: "9px 12px",
  fontSize: 13,
  fontWeight: 700,
  color: "var(--audit-text-muted)",
  borderBottom: "1px solid var(--audit-border-strong)",
  whiteSpace: "nowrap",
};
function GridTh({ children, align = "center" }) {
  return <th style={{ ...gridThBase, textAlign: align }}>{children}</th>;
}
const gridTdBase = {
  padding: "9px 12px",
  borderBottom: "1px solid var(--audit-border)",
  verticalAlign: "top",
};
const gridTdMono = {
  ...gridTdBase,
  fontFamily: "JetBrains Mono, monospace",
  fontWeight: 700,
  color: "var(--audit-text)",
  textAlign: "center",
  fontSize: 13,
  whiteSpace: "nowrap",
};
const gridTdName = {
  ...gridTdBase,
  color: "var(--audit-text)",
  fontSize: 13,
  lineHeight: "19px",
  wordBreak: "break-word",
};
const gridTdGubun = {
  ...gridTdBase,
  textAlign: "center",
  fontSize: 12.5,
  color: "var(--audit-text-muted)",
  // 구분은 대부분 한글(품목분류N과-NNNN) — mono 폴백은 자간이 뭉쳐 보임. 비율 폰트.
  fontFamily: "inherit",
  wordBreak: "break-word",
};
const gridTdDate = {
  ...gridTdBase,
  textAlign: "center",
  fontSize: 12,
  color: "var(--audit-text-muted)",
  fontFamily: "JetBrains Mono, monospace",
  whiteSpace: "nowrap",
};

// ── 상세 표 스타일 ──────────────────────────────────────────
const thStyle = {
  textAlign: "left",
  verticalAlign: "top",
  padding: "5px 10px 5px 0",
  width: 88,
  color: "var(--audit-text-muted)",
  fontSize: 13,
  fontWeight: 600,
  whiteSpace: "nowrap",
  borderBottom: "1px solid var(--audit-border)",
};
const tdStyle = {
  verticalAlign: "top",
  padding: "5px 0",
  color: "var(--audit-text)",
  borderBottom: "1px solid var(--audit-border)",
  wordBreak: "break-word",
};

function Th({ children }) {
  return <th style={thStyle}>{children}</th>;
}

function Centered({ children }) {
  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      {children}
    </div>
  );
}
